import {api} from '../../contexts/AuthContext';
import {BrandSafetyPrompt, PromptPaginatedResponse} from '../../types';

export type {PromptPaginatedResponse};

// Prompt API pagination types (offset-based)
export interface PromptPaginationParams {
    page?: number;
    pageSize?: number;
    search?: string;
    companyId?: string;
    // "Show only my blueprints" — when true, the backend scopes the list
    // strictly to the authenticated user's id, ignoring company scoping
    // (bd deepcontext-zh7jq).
    onlyMine?: boolean;
}

// Prompt API
export const promptApi = {
    // Paginated fetch.
    // Accepts an optional `signal` so callers can cancel stale requests
    // (e.g. when the user keeps typing in a search input). The signal is
    // forwarded directly to axios so the cancel actually reaches the
    // network layer — without it, in-flight requests run to completion
    // and clobber state with last-writer-wins (bd-sqi).
    getAllPaginated: async (
        params: PromptPaginationParams = {},
        options: { signal?: AbortSignal } = {}
    ): Promise<PromptPaginatedResponse> => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.set('page', params.page.toString());
        if (params.pageSize) queryParams.set('pageSize', params.pageSize.toString());
        if (params.search) queryParams.set('search', params.search);
        if (params.companyId) queryParams.set('companyId', params.companyId);
        if (params.onlyMine) queryParams.set('onlyMine', 'true');

        const queryString = queryParams.toString();
        const response = await api.get(
            `/api/prompts${queryString ? `?${queryString}` : ''}`,
            { signal: options.signal }
        );
        return {
            prompts: response.data.prompts,
            pagination: response.data.pagination,
            cached: response.data.cached
        };
    },

    // Legacy non-paginated fetch (for backward compatibility).
    // Accepts an optional `signal` so callers can cancel a stale in-flight
    // fetch (e.g. the unscoped request that fires before the caller's
    // companyId resolves) — bd deepcontext-jvwh.1.
    getAll: async (
        companyId?: string,
        options: { signal?: AbortSignal } = {}
    ): Promise<BrandSafetyPrompt[]> => {
        const params = companyId ? `?companyId=${companyId}&pageSize=1000` : '?pageSize=1000';
        const response = await api.get(`/api/prompts${params}`, { signal: options.signal });
        return response.data.prompts;
    },

    // companyId lets staff read blueprints of the selected company that they
    // did not create; the creator can always read their own blueprint
    // regardless of companyId (canUserReadPrompt on the backend).
    getById: async (id: string, companyId?: string): Promise<BrandSafetyPrompt> => {
        const params = companyId ? `?companyId=${companyId}` : '';
        const response = await api.get(`/api/prompts/${id}${params}`);
        return response.data.prompt;
    },

    create: async (prompt: Omit<BrandSafetyPrompt, 'id' | 'createdAt' | 'updatedAt'> & {
        syncToIndex?: boolean
    }): Promise<BrandSafetyPrompt> => {
        const response = await api.post('/api/prompts', prompt);
        return response.data.prompt;
    },

    update: async (id: string, prompt: Partial<Omit<BrandSafetyPrompt, 'id' | 'createdAt' | 'updatedAt'>> & {
        syncToIndex?: boolean
    }): Promise<BrandSafetyPrompt> => {
        const response = await api.put(`/api/prompts/${id}`, prompt);
        return response.data.prompt;
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/api/prompts/${id}`);
    },

    rewriteWithAI: async (prompt: string): Promise<{
        rewrittenPrompt: string;
        originalPrompt: string;
    }> => {
        const response = await api.post('/api/prompts/rewrite', {prompt});
        return response.data;
    },

    // Generate a new blueprint prompt with AI from a brand URL + target
    // countries (bd deepcontext-0445.2), instead of rewriting existing text.
    // Brand URL is a generate-time input only — it is not persisted.
    generateWithAI: async (brandUrl: string, countries: string[]): Promise<{
        generatedPrompt: string;
    }> => {
        const response = await api.post('/api/prompts/generate', {brandUrl, countries});
        return response.data;
    },

    // On-demand match-insights advisory (bd bqw.9 + bqw.17). Re-samples recent
    // unmatched URLs from the blueprint's scheduled-job history, asks the LLM
    // for a structured response with both a short advisory narrative and a
    // theme-count breakdown of rejection patterns. Each call re-runs the LLM —
    // no caching.
    getMatchInsights: async (promptId: string): Promise<{
        status: 'ok' | 'no_unmatched' | 'llm_error' | 'error';
        narrative: string | null;
        themes?: Array<{
            label: string;
            count: number;
            sampleUrls: string[];
        }>;
        sampleSize?: number;
        runsConsidered?: number;
        generatedAt?: string;
        error?: string;
    }> => {
        const response = await api.post(`/api/prompts/${promptId}/match-insights`);
        return response.data;
    },

    // Delivery-funnel payload (bd be0.3). Auth-gated, Redis-cached for 10min.
    // Defaults to last 7 days when `from`/`to` are omitted, and to "All campaigns"
    // when `campaignId` is null/undefined. The backend rejects ranges > 30 days with 400.
    getDeliveryFunnel: async (
        promptId: string,
        opts: { campaignId?: number | null; from?: string; to?: string } = {}
    ): Promise<import('../../types/deliveryFunnel').DeliveryFunnelPayload> => {
        const params = new URLSearchParams();
        if (opts.campaignId !== undefined && opts.campaignId !== null) {
            params.set('campaign_id', String(opts.campaignId));
        }
        if (opts.from) params.set('from', opts.from);
        if (opts.to) params.set('to', opts.to);
        const qs = params.toString();
        const response = await api.get(
            `/api/prompts/${promptId}/delivery-funnel${qs ? `?${qs}` : ''}`
        );
        return response.data;
    },

    checkIndexSegment: async (promptId: string): Promise<{ exists: boolean; segment?: any }> => {
        const response = await api.get(`/api/prompts/${promptId}/index-segment`);
        return response.data;
    },

    checkEquativSegment: async (promptId: string): Promise<{
        exists: boolean;
        publicExists?: boolean;
        segment?: any;
        publicSegment?: any
    }> => {
        const response = await api.get(`/api/prompts/${promptId}/equativ-segment`);
        return response.data;
    },

    checkPubmaticSegment: async (promptId: string): Promise<{ exists: boolean; segment?: any }> => {
        const response = await api.get(`/api/prompts/${promptId}/pubmatic-segment`);
        return response.data;
    },

};
